import React, { useState, useEffect } from 'react';
import { apiRoutes } from 'api_routes/api_routes';
import styled from 'styled-components';
import GridTemplate from 'templates/GridTemplate';
import Select from '@mui/material/Select';
import Label from 'components/atoms/Label/Label';
import { theme } from 'theme/MainTheme';
import attachment from 'assets/icons/paperclip.svg';
import axiosInstance from 'api/axios';
import ButtonMUI from 'components/atoms/Button/ButtonMUI';
import { Button } from '@mui/material';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div`
  padding: 30px 0;
  @media (min-width: 1000px) {
    max-width: 40vw;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledGrid = styled.div`
  padding: 24px 30px;
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: 15px 40px;
  @media (max-width: 1500px) {
    grid-gap: 10px;
    grid-template-columns: repeat(1, 1fr);
  }
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const ContentWrapper = styled.div`
  align-items: center;
  justify-content: space-between;
`;

const AttachmentWrapper = styled.div`
  margin: 0;
`;

const AttachmentContainer = styled.div`
  color: ${theme.grey300};
  font-size: 1.3rem;
  font-weight: bold;
`;

const StyledLinkButton = styled.a`
  display: block;
  width: 47px;
  height: 47px;
  background: white url(${attachment}) no-repeat;
  background-size: 70%;
  background-position: 50%;
  cursor: pointer;
`;

const ContactForm = () => {
  const { t } = useTranslation();
  const [isFormSend, setIsFormSend] = useState(false);
  const [isReminderBlocked, setIsReminderBlocked] = useState(false);
  const [isReminderOk, setIsReminderOk] = useState(false);
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState();
  const [documentsList, setDocumentsList] = useState();
  const [form, setForm] = useState({
    message: '',
    group_tag: '',
    geid: localStorage.getItem('user'),
    file: [],
    file_date: null,
  });

  const getData = async () => {
    axiosInstance.get(`${apiRoutes.get_user_files}${localStorage.getItem('user')}`).then((res) => {
      const docList = [];
      res.data.forEach((el) => docList.push({ label: el.path.split('\\').pop(), id: el.id }));
      setDocumentsList(docList);
    });
  };

  useEffect(() => {
    getData();
  }, []);

  const paymentTag = 'user_panel_payment_data_error';

  const updateField = (e) => {
    if (e.target.name === 'file') {
      const files = form.file.push(e.target.files[0]);
      setForm({
        ...form,
        [e.target.file]: files,
      });
    } else {
      setForm({
        ...form,
        [e.target.name]: e.target.value,
      });
      setError(null);
    }
  };

  const validate = () => {
    if (form.group_tag === paymentTag && !form.file_date) {
      return t('select_document');
    }
    if (!form.group_tag) {
      return t('subject_err_info');
    }
    if (!form.message) {
      return t('description_err_info');
    }
    if (form.file.length > 0) {
      const fileSize = form.file.map((elem) => elem.size).reduce((a, b) => a + b);
      const errTypeFiles = form.file.filter((file) => file.type !== 'application/pdf');

      if (fileSize > 5000000) {
        return t('max_file_size');
      }
      if (errTypeFiles.length > 0) {
        return t('allowed_file_format');
      }
    }
    return null;
  };

  const remindDocumentKey = () => {
    axiosInstance
      .post('/v1/remindDocumentKey', {
        geid: form.geid,
      })
      .then((res) => {
        if (res.status === 200) {
          if (res.data.code === 200) {
            setIsReminderOk(true);
            setMobile(res.data.mobile);
          }
          if (res.data.code === 400) {
            setIsReminderOk(false);
            setError(t('key_reminder_errr_info'));
          }
          if (res.data.code === 425) {
            setIsReminderOk(false);
            setIsReminderBlocked(true);
            setError(t('key_reminder_blocked'));
          }
        }
      })
      .catch((err) => {
        if (err.response) {
          setError(t('key_reminder_errr_info'));
        }
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errorMsg = validate(form);
    if (errorMsg) {
      setError(errorMsg);
      return;
    }

    const formData = new FormData();
    form.file.forEach((file) => formData.append('file', file));
    formData.append('group_tag', form.group_tag);
    formData.append('message', form.message);
    formData.append('file_date', form.file_date);
    formData.append('geid', localStorage.getItem('user'));

    try {
      axiosInstance.post('/v1/mail/send', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setError(null);
      setIsFormSend(true);
      setForm({
        message: '',
        group_tag: '',
        file: [],
        file_date: null,
      });
    } catch {
      setError(t('form_send_err_info'));
    }
  };

  const removeFile = (name) => {
    const fileUpdt = form.file?.filter((el) => el.name !== name);
    setForm({
      ...form,
      file: fileUpdt,
    });
  };

  return (
    <GridTemplate pageType={t('form_header')}>
      <Wrapper>
        {error && (
          <Stack sx={{ width: '100%', paddingTop: '20px' }} spacing={0}>
            <Alert severity="error" sx={{ alignItems: 'center', fontSize: '1.2rem' }}>
              {error}
            </Alert>
          </Stack>
        )}
        {isReminderOk && (
          <Stack sx={{ width: '100%', paddingTop: '20px' }} spacing={0}>
            <Alert severity="success" sx={{ alignItems: 'center', fontSize: '1.2rem' }}>
              {t('key_reminder_ok_info', { mobile })}
            </Alert>
          </Stack>
        )}
        {!isFormSend ? (
          <form onSubmit={handleSubmit}>
            <StyledGrid>
              <ContentWrapper>
                <FormControl
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-shrink': {
                      top: '-5px',
                    },
                  }}
                >
                  <InputLabel id="demo-simple-select-label">{t('select_subject')}</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    name="group_tag"
                    value={form.group_tag}
                    label="Temat"
                    onChange={updateField}
                  >
                    <MenuItem key="1" value="user_panel_personaldata_update">
                      {t('data_update')}
                    </MenuItem>
                    <MenuItem key="2" value="user_panel_payment_data_error">
                      {t('payment_err')}
                    </MenuItem>
                    <MenuItem key="3" value="user_panel_phone_error">
                      {t('phone_err')}
                    </MenuItem>
                  </Select>
                </FormControl>
              </ContentWrapper>
              {form.group_tag === paymentTag ? (
                <ContentWrapper>
                  <Autocomplete
                    name="file_date"
                    fullWidth
                    disablePortal
                    id="combo-box-demo"
                    noOptionsText={t('no_option')}
                    options={documentsList}
                    onChange={(event, newValue) => {
                      setForm({ ...form, file_date: newValue.label });
                      setError(null);
                    }}
                    sx={{ mt: 1.5 }}
                    renderInput={(params) => <TextField {...params} label={t('select_document')} />}
                  />
                </ContentWrapper>
              ) : null}
              <ContentWrapper>
                <TextField
                  fullWidth
                  id="outlined-multiline-static"
                  label={t('description')}
                  multiline
                  rows={4}
                  name="message"
                  sx={{ mt: 1.5 }}
                  onChange={updateField}
                />
              </ContentWrapper>
              <AttachmentWrapper>
                <input
                  id="fileUpload"
                  name="file"
                  type="file"
                  accept="application/pdf"
                  multiple
                  style={{ display: 'none' }}
                  onChange={updateField}
                />
                <Label htmlFor="fileUpload">
                  {t('add_attachment')}
                  <StyledLinkButton />
                </Label>
                <AttachmentContainer className="uploaded-files-list">
                  {form.file
                    ? form.file.map((file) => (
                        <div key={file.id}>
                          {file.name}
                          <IconButton
                            edge="end"
                            aria-label="close"
                            onClick={() => removeFile(file.name)}
                          >
                            <CloseIcon />
                          </IconButton>
                        </div>
                      ))
                    : null}
                </AttachmentContainer>
              </AttachmentWrapper>
              <ButtonWrapper>
                <ButtonMUI type="submit" value={t('send')} />
              </ButtonWrapper>
              <ButtonWrapper>
                <Button
                  type="button"
                  variant="contained"
                  disabled={isReminderBlocked}
                  color="error"
                  sx={{ width: '150px', marginTop: '100px' }}
                  onClick={remindDocumentKey}
                >
                  {t('key_reminder')}
                </Button>
              </ButtonWrapper>
            </StyledGrid>
          </form>
        ) : (
          <Stack sx={{ width: '100%', paddingTop: '20px' }} spacing={0}>
            <Alert severity="success" sx={{ alignItems: 'center', fontSize: '1.2rem' }}>
              {t('form_send_info1')} <br /> {t('form_send_info2')}
            </Alert>
          </Stack>
        )}
      </Wrapper>
    </GridTemplate>
  );
};
export default ContactForm;
